import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import { useFormikContext } from "formik";
import Filter from "library/Molecules/Filter";
import Menu from "library/Molecules/Menu";
import PropTypes from "prop-types";
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { retrieveContact } from "store/features/contact.store";
import { prettifyStatusLabel } from "utility/helpers/general";
import {
  DELETED_INVOICE_STATUS,
  INACTIVE_INVOICE_STATUS,
  INVOICE_STATUSES_LIST,
  ORDER_STATUSES_LIST,
} from "../../../../constants";

const StatusFilter = ({ isOrder }) => {
  const { t } = useTranslation();

  const {
    values: { status = "all" },
    setFieldValue,
  } = useFormikContext();

  const contact = useSelector(retrieveContact);

  const statusFilterRef = useRef();

  const statusFilterOptions = useMemo(() => {
    return ORDER_STATUSES_LIST.map((value) => {
      return { label: t([`utils.deliveryStatuses.${value}`, prettifyStatusLabel(value)]), value };
    });
  }, [t]);

  const invoiceFilterOptions = useMemo(() => {
    const isAdmin = contact.roles?.includes("pedivanAdmin");

    return INVOICE_STATUSES_LIST.filter((s) =>
      isAdmin ? true : ![INACTIVE_INVOICE_STATUS, DELETED_INVOICE_STATUS].includes(s)
    ).map((value) => {
      return { label: t([`utils.invoiceStatuses.${value}`, prettifyStatusLabel(value)]), value };
    });
  }, [contact.roles, t]);

  const options = useMemo(() => {
    const defaultOption = { label: t("features.filtersForm.filterPlaceholder"), value: "all" };
    return [defaultOption, ...(isOrder ? statusFilterOptions : invoiceFilterOptions)];
  }, [invoiceFilterOptions, isOrder, statusFilterOptions, t]);

  const handleChangeStatus = useCallback(
    (value) => {
      statusFilterRef.current?.closeMenu?.();
      if (status === value) return;
      setFieldValue("status", value);
    },
    [status, setFieldValue]
  );

  return (
    <>
      <Filter
        id="list-status-filter"
        ref={statusFilterRef}
        badge={status !== "all"}
        icon={<FilterListRoundedIcon fontSize="small" />}
        data-value={`value-${status}`}
      >
        <>
          <Menu.MenuItem className="mx-3 mb-2 px-4" disabled>
            {t("features.filtersForm.statusFilterPlaceholder")}
          </Menu.MenuItem>

          {options.map(({ label, value }) => (
            <Menu.MenuItem
              key={value}
              selected={status === value}
              onClick={() => handleChangeStatus(value)}
              aria-selected={status === value}
              data-value={value}
            >
              <span className="text-fs-12 pl-3">{label}</span>
            </Menu.MenuItem>
          ))}
        </>
      </Filter>
    </>
  );
};

StatusFilter.propTypes = {
  isOrder: PropTypes.bool.isRequired,
};

export default StatusFilter;
